import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Paper, Typography, WithStyles, withStyles } from "@material-ui/core";
import { styles } from "./RegisterUserOnBehalfView.styles"; 
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import * as UserRegistrationAction from "../../../../stores/user/UserRegistrationAction";
import { RegisterForm } from "../../registration/RegisterForm";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { IAgrees } from "../../AcceptTermsConditionsComponent";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import { selectVisibleCountries } from "../../../../selectors/countries/CountriesSelector";

const agreeTypes: IAgrees = {
    agreeClientLegal: false
};

export interface IRegisterUserOnBehalfViewProps extends WithStyles<typeof styles> {
}

const RegisterUserOnBehalfViewPure: React.FunctionComponent<IRegisterUserOnBehalfViewProps> = ({ classes }) => {

    const {t} = useTranslation();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [errors, setErrors] = useState([]);
    const [registerError, setRegisterError] = useState<string>("");

    const countries = useSelector(state => selectVisibleCountries(state));

    const [details, setDetails] = React.useState({
        email: "",
        firstName: "",
        lastName: "",
        jobPosition: "",
        phone: "",
        mobilePhone: "",
        company: "",
        tin: "",

        addressLine1: "",
        addressLine2: "",
        postcode: "",
        city: "",
        country: "",

        deliveryAddressLine1: "",
        deliveryAddressLine2: "",
        deliveryPostcode: "",
        deliveryCity: "",
        deliveryCountry: "",
        isCreatedByAdmin: true
    });

    const handleChange = (event, isFieldValid) => {
        const name = event.target.name;
        const value = event.target.value;

        setDetails({
            ...details,
            [name]: value
        });

        if (isFieldValid) {
            setErrors(errors.filter(e => e !== name));
        } else {
            setErrors(errors.concat(name));
        }
    };

    const handleCountry = event => {
        if (event.target.value !== "") {
            setDetails({
                ...details,
                [event.target.name]: event.target.value
            });
        }
    };

    const handleSubmit = () => {
            let sendDetails = details;
            if (anotherDeliveryAddress === false) {
                sendDetails.deliveryAddressLine1 = details.addressLine1;
                sendDetails.deliveryAddressLine2 = details.addressLine2;
                sendDetails.deliveryCity = details.city;
                sendDetails.deliveryPostcode = details.postcode;
                sendDetails.deliveryCountry = details.country;
            }
            dispatch(UserRegistrationAction.registerUser(sendDetails))
                .then(() => {
                    enqueueSnackbar("User registered on behalf", { variant: ToastStatusEnum.Info });
                    setRegisterError("");
                    history.push({
                        pathname: "/admin/register-on-behalf/success"
                    });
                })
                .catch(e => {
                    setRegisterError(e.message);
                });
    };

    const [agrees, setAgrees] = React.useState(
        agreeTypes || {
            agreeClientLegal: false
        }
    );

    const handleAgree = event => {
        setAgrees({
            ...agrees,
            [event.target.name]: !agrees[event.target.name]
        });
    };

    const formIsValid = (): boolean => {
        let isValid =
            !errors.includes("firstName") &&
            !errors.includes("lastName") &&
            !errors.includes("addressLine1") &&
            !errors.includes("postcode") &&
            !errors.includes("city") &&
            !errors.includes("company") &&
            details.firstName?.length !== 0 &&
            details.lastName?.length !== 0 &&
            details.addressLine1?.length !== 0 &&
            details.postcode?.length !== 0 &&
            details.city?.length !== 0 &&
            details.company?.length !== 0 &&
            details.country?.length !== 0;

        isValid = isValid && agrees.agreeClientLegal && !errors.includes("email") && details.email?.length !== 0;

        return isValid;
    };

    const [anotherDeliveryAddress, setAnotherDeliveryAddress] = React.useState(false);

    return (
        <>
            <CenteredContainer>
                <Breadcrumbs
                    items={[
                        {title: t("admin.adminPanel"), href: "/admin"},
                        {title: "Manage users", href: "/admin/manage-users"},
                        {
                            title: "Register User on Behalf"
                        }
                    ]}
                />
                <div className={classes.title}>
                    <Typography variant="h2">Register User on Behalf</Typography>
                </div>
                <Paper className={classes.formBox}>
                    <Grid container spacing={1}>
                        <RegisterForm
                            type="addNewUserAdmin"
                            details={details}
                            registerError={registerError}
                            handleChange={handleChange}
                            handleCountry={handleCountry}
                            allCountries={countries}
                            anotherDeliveryAddress={anotherDeliveryAddress}
                            setAnotherDeliveryAddress={setAnotherDeliveryAddress}
                            agrees={agrees}
                            handleAgree={handleAgree}/>
                    </Grid>
                </Paper>
                <BlueButton
                    disabled={!formIsValid()}
                    onClick={handleSubmit}
                >Register User
                </BlueButton>
            </CenteredContainer>
        </>
    );
};

export const RegisterUserOnBehalfView = withStyles(styles)(RegisterUserOnBehalfViewPure);